import DayJsAdapter from "@date-io/dayjs";
import "@mdi/font/css/materialdesignicons.css";
import { enGB } from "date-fns/locale";
import { createVuetify } from "vuetify";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import { VDateInput } from "vuetify/labs/VDateInput";
// import { VCalendar } from "vuetify/labs/VCalendar";
import VueDraggable from "vuedraggable";
import { VSnackbarQueue } from "vuetify/components";
import "vuetify/styles";

import CheckboxField from "@/components/ui/inputs/CheckboxField.vue";
import DateFieldValidator from "@/components/shared/validators/DateFieldValidator.vue";
// import SelectField from "@/components/ui/inputs/SelectField.vue";
import SelectFieldValidator from "@/components/shared/validators/SelectFieldValidator.vue";
import TextField from "@/components/ui/inputs/TextField.vue";
import TextFieldValidator from "@/components/shared/validators/TextFieldValidator.vue";
import UploadField from "@/components/ui/inputs/UploadField.vue";
import TextAreaField from "@/components/ui/inputs/TextAreaField.vue";
import JsonViewer from "@/components/ui/inputs/JsonViewer.vue";
import Loading from "@/components/shared/Loading.vue";

/** Elevated data-table colors (formerly hardcoded in elevatedTable.scss). */
export const ELEVATED_TABLE_THEME_COLORS = {
  light: {
    "table-elevated-header": "#fafbfc",
    "table-elevated-row": "#ffffff",
    "table-elevated-border": "#eeeeee",
    "table-elevated-border-hover": "#e0e0e0",
    /** Base color for row hover shadow — opacity applied in elevatedTable.scss */
    "table-elevated-shadow": "#0f172a",
  },
  dark: {
    "table-elevated-header": "#2a2b38",
    "table-elevated-row": "#1e1e2a",
    "table-elevated-border": "#38394a",
    "table-elevated-border-hover": "#4a4d5e",
    "table-elevated-shadow": "#000000",
  },
} as const;

const orbisLightTheme = {
  dark: false,
  colors: {
    shadow: "#000000",
    surface: "#ffffff",
    background: "#F2F2F8",
    "background-secondary": "#ffffff",
    "surface-muted": "#f8fafc",
    "surface-subnav": "#FAFAFC",
    primary: "#253D4E",
    "on-primary": "#ffffff",
    primaryLighter: "#315762",
    "on-surface": "#0E2B3A",
    secondary: "#009C89",
    secondaryDarker: "#00786A",
    success: "#56BF85",
    notice: "#115265",
    tertiary: "#115265",
    quaternary: "#6A5BC1",
    purple: "#6A5B99",
    lightPurple: "#5d6f9a",
    error: "#E15E54",
    warning: "#F69334",
    neutral: "#9CA3AF",
    teal: "#009C89",
    commBlue: "#00BEFF",
    pastelBlue: "#2563EB",
    link: "#007ac1",
    elevated: "#FFFFFF",
    fileUpload: "#FBFBFF",
    "admin-settings": "#334D7F",
    "admin-settings-sub": "#2B4E90",

    unuser: "#69A4D5",
    phase01: "#015366",
    phase02: "#00777F",
    phase03: "#009C89",
    phase04: "#56BF85",
    phase05: "#F5A53A",
    phase06: "#F5A53A",
    phaseDark: "#C6CBDE",
    liteError: "#fff4f1",
    liteError2: "#fff8f5",
    liteError3: "#fceeeb",
    liteWarning: "#FFD6CE",
    liteWarning2: "#fffaf6",
    liteWarning3: "#fffbf9",
    liteWarning4: "#fff3e7",
    liteSubwarn: "#FEEFE0",
    liteBlue: "#dee3f9",
    liteGreen: "#E8F7F2",
    liteDef: "#F2F2F8",
    liteDef2: "#edeef5",
    liteDef3: "#e1e2ec",
    liteDef4: "#fbfbfd",
    liteDef5: "#cbcbcd",
    liteDef6: "#f4f5fb",
    liteGreen2: "#eaf2f5",
    liteGreen3: "#719d97",
    liteGreen4: "#dae8ee",
    "surface-light": "#f4f5fb",
    "surface-variant": "#e1e2ec",
    "on-surface-variant": "#253D4E",
    text: "#253D4E",
    "on-background": "#253D4E",
    "flow-connection-and": "#000000",
    "flow-connection-or": "#000000",

    "dm-widget-background": "#E9F0EE",

    ...ELEVATED_TABLE_THEME_COLORS.light,
  },
};

const orbisDarkTheme = {
  dark: true,
  colors: {
    surface: "#1E1E2A",
    background: "#1A1A24",
    "background-secondary": "#1E1E2A",
    "surface-muted": "#2E2F40",
    "surface-subnav": "#2E2F40",
    primary: "#D1E4ED",
    "on-primary": "#253D4E",
    primaryLighter: "#E8F2F7",
    shadow: "#000000",
    "on-surface": "#F4F8FB",
    secondary: "#00D2BA",
    secondaryDarker: "#00786A",
    success: "#56BF85",
    notice: "#3CB4C8",
    tertiary: "#3CB4C8",
    quaternary: "#B7A8FF",
    purple: "#C5B8E1",
    lightPurple: "#AAB8D1",
    error: "#FF867C",
    warning: "#FFB65A",
    neutral: "#374151",
    teal: "#00D2BA",
    commBlue: "#00D6FF",
    pastelBlue: "#7EA6FF",
    link: "#4FB0E8",
    elevated: "#6e6e6e",
    fileUpload: "#1E1E2A",
    "admin-settings": "#374763",
    "admin-settings-sub": "#5E85D4",

    unuser: "#8FC4F0",
    phase01: "#388E9F",
    phase02: "#38A3B1",
    phase03: "#38B0A0",
    phase04: "#62B085",
    phase05: "#C99636",
    phase06: "#C99636",
    phaseDark: "#8C91AD",
    liteError: "#3B1F1E",
    liteError2: "#452624",
    liteError3: "#5C2A27",
    liteWarning: "#4A2E1E",
    liteWarning2: "#3F352B",
    liteWarning3: "#4D3A30",
    liteWarning4: "#5C3F24",
    liteSubwarn: "#56442E",
    liteBlue: "#2E3246",
    liteGreen: "#233D38",
    liteDef: "#1E1E2A",
    liteDef2: "#2B2C3A",
    liteDef3: "#363848",
    liteDef4: "#2A2B38",
    liteDef5: "#454A5F",
    liteDef6: "#2E2F40",
    liteGreen2: "#2A3B3F",
    liteGreen3: "#B2D5CD",
    liteGreen4: "#324247",
    "surface-light": "#2E2F40",
    "surface-variant": "#D1E4ED",
    "on-surface-variant": "#253D4E",
    text: "#F4F8FB",
    "on-background": "#F4F8FB",
    "flow-connection-and": "#ffffff",
    "flow-connection-or": "#ffffff",

    "dm-widget-background": "#2E2F40",

    ...ELEVATED_TABLE_THEME_COLORS.dark,
  },
};

export default createVuetify({
  date: {
    adapter: DayJsAdapter,
    locale: { en: enGB },
  },
  icons: {
    defaultSet: "mdi", // Set default icon set (Material Design Icons)
    aliases,
    sets: { mdi }, // Include MDI set
  },
  display: {
    thresholds: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      xxl: 1536,
    },
  },
  theme: {
    defaultTheme: "orbisLightTheme",
    themes: {
      orbisLightTheme,
      orbisDarkTheme,
    },
  },
  components: {
    VDateInput,
    CheckboxField,
    // SelectField,
    SelectFieldValidator,
    TextField,
    TextFieldValidator,
    UploadField,
    DateFieldValidator,
    TextAreaField,
    VueDraggable,
    JsonViewer,
    // VCalendar,
    Loading,
    VSnackbarQueue,
  },
  defaults: {
    DateTimeField: {
      datetime: true,
    },
    VBtn: {
      class: "tw:normal-case tw:text-ms tw:tracking-normal",
    },
    VCard: {
      elevation: "0",
      rounded: "lg",
    },
    // Every input renders a VField, so this rounds text fields, selects, autocompletes and
    // pickers alike to the same 8px the buttons and cards already use.
    VField: {
      rounded: "lg",
    },
    VDatePicker: {
      class: "bg-surface",
    },
    VTimePicker: {
      class: "bg-surface",
    },
    VSkeletonLoader: {
      color: "transparent",
      type: "article",
    },
    VOverlay: {
      opacity: 0.65,
      // One global handler in setupOverlayBackGuard — not per-overlay router guards.
      closeOnBack: false,
    },
  },
});
